import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatUsd, type LanguageDepth } from "@veyso/core";
import { ApiError, api, type LanguageView, type LanguagesView } from "../api.js";
import { KeepTerms } from "./KeepTerms.js";
import { BILLING_CHANGED } from "../components/BillingChip.js";
import { ErrorNote } from "../components/ui.js";
import { ProgressLine } from "../components/ProgressLine.js";
import { languageStepLabel } from "../languages/languageSteps.js";
import { DiscardButton } from "../languages/DiscardButton.js";
import {
  anyInProgress,
  blockedMessage,
  downloadLinks,
  isInProgress,
  makeBody,
  offerLabel,
  previousOffer,
  priceFor,
  priceLabel,
  raiseBody,
  retryBody,
  sameLanguages,
  totalPrice,
  voiceOnlyNote,
} from "../languages/languages.js";

const POLL_MS = 5_000;

const billingChanged = () => window.dispatchEvent(new Event(BILLING_CHANGED));

/** A refusal, said beside what caused it; a credit one carries the way to add some. */
type Refusal = { message: string; credit: boolean };

function refusalOf(cause: unknown): Refusal {
  if (cause instanceof ApiError && (cause.code === "insufficient_credit" || cause.code === "no_credit")) {
    return { message: "Not enough credit — add credit to make these languages.", credit: true };
  }
  return { message: "That didn't work. Try again in a moment.", credit: false };
}

const lineWord = (count: number) => (count === 1 ? "1 line runs long" : `${count} lines run long`);

/**
 * The editor's Languages panel: pick languages and how much to translate,
 * then watch each one being made and act on it. The editor itself is where a
 * script is checked and a language looked at; this only starts, approves,
 * updates and discards them.
 *
 * With `version` set (a launch video) the whole video is offered only where
 * that version's picture can be drawn again — a film made before its page
 * was kept is voice and subtitles.
 */
export const LanguagesPanel: React.FC<{
  projectId: string;
  /** A launch video's version; omitted for a product demo. */
  version?: string;
  view: LanguagesView | null;
  /** Reads the languages again. */
  onChanged: () => void;
  onClose: () => void;
  /** Asks the editor to show this language, its script open for checking. */
  onCheckScript: (code: string) => void;
  /**
   * A launch version's offer: the ready version before it and the languages it
   * was made in, chosen for this version too while it has none of its own.
   */
  previous?: { number: number; languages: LanguageView[] } | null;
  /** This launch version's number, for the offer's words. */
  versionNumber?: number;
}> = ({ projectId, version, view, onChanged, onClose, onCheckScript, previous, versionNumber }) => {
  const [chosen, setChosen] = useState<string[]>([]);
  const [depth, setDepth] = useState<LanguageDepth>("video");
  const [review, setReview] = useState(true);
  const [working, setWorking] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);
  const offerAsked = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // A language made meanwhile elsewhere is no longer on offer.
  const availableCodes = view?.available.map((one) => one.code).join(",");
  useEffect(() => {
    setChosen((current) => {
      const next = current.filter((code) => availableCodes?.split(",").includes(code));
      return next.length === current.length ? current : next;
    });
  }, [availableCodes]);

  // A new launch version has no languages. Once its own list is read and is
  // empty, the previous version's are chosen for it — once, so a customer who
  // clears the choice is not given it back on the next read.
  const offered =
    version && previous && view && view.canTranslate && view.languages.length === 0
      ? previousOffer(previous.languages, view.available)
      : [];
  const offeredCodes = offered.map((one) => one.code).join(",");
  useEffect(() => {
    if (offerAsked.current || offeredCodes === "") return;
    offerAsked.current = true;
    setChosen((current) => (current.length > 0 ? current : offeredCodes.split(",")));
  }, [offeredCodes]);

  /** Every ready language as one zip, by a link the API signs for a few minutes. */
  const downloadAll = async () => {
    setZipError(null);
    setZipping(true);
    try {
      const { url } = await api.languagesDownloadLink(projectId, version);
      if (alive.current) window.location.assign(url);
    } catch (cause) {
      if (alive.current) setZipError((cause as Error).message);
    } finally {
      if (alive.current) setZipping(false);
    }
  };

  const polling = anyInProgress(view);
  const reload = useRef(onChanged);
  reload.current = onChanged;
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => reload.current(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling]);

  /** One request that changes languages, then a fresh read whatever came of it. */
  const act = async (request: () => Promise<unknown>) => {
    setRefusal(null);
    setWorking(true);
    try {
      await request();
      if (alive.current) billingChanged();
    } catch (cause) {
      if (alive.current) setRefusal(cause instanceof ApiError && cause.status === 409 ? null : refusalOf(cause));
    } finally {
      if (alive.current) setWorking(false);
      onChanged();
    }
  };

  const blocked =
    view && !view.canTranslate ? (blockedMessage(view.reason) ?? "Languages can't be made for this video.") : null;
  const available = view?.available ?? [];
  const picked = available.filter((one) => chosen.includes(one.code));
  // A launch version offers the whole video only when its picture can be drawn again.
  const wholeOffered = !version || view?.canDrawWords === true;
  const effective: LanguageDepth = wholeOffered ? depth : "voice";
  const total = totalPrice(available, chosen, effective);
  // A free-forever team is charged nothing, so every price reads "Free".
  const free = view?.freeForever === true;
  const short = !free && view?.balanceUsd != null && total > view.balanceUsd;
  const note = wholeOffered ? voiceOnlyNote(picked, depth) : null;

  // The per-language price on each depth card: the first chosen language's, or the cheapest on offer.
  const sample = picked[0];
  const cardPrice = (which: LanguageDepth) =>
    sample
      ? priceFor(sample, which)
      : available.length > 0
        ? Math.min(...available.map((one) => priceFor(one, which)))
        : null;

  // "N × $a + M × $b": languages grouped by the price each will cost.
  const breakdown = [
    ...picked.reduce((groups, one) => {
      const price = priceFor(one, effective);
      groups.set(price, (groups.get(price) ?? 0) + 1);
      return groups;
    }, new Map<number, number>()),
  ]
    .sort((a, b) => b[0] - a[0])
    .map(([price, count]) => `${count} × ${priceLabel(price, free)}`)
    .join(" + ");

  const toggle = (code: string) =>
    setChosen((current) => (current.includes(code) ? current.filter((one) => one !== code) : [...current, code]));

  const translate = () =>
    void act(async () => {
      await api.makeLanguages(projectId, makeBody(chosen, review, effective, version));
      if (alive.current) setChosen([]);
    });

  return (
    <aside
      aria-label="Languages"
      className="flex h-full min-h-0 flex-col border-l border-[var(--edge)] bg-bay-900 shadow-[-12px_0_24px_rgba(16,21,28,0.06)]"
    >
      <div className="flex items-center gap-2.5 border-b border-[var(--edge)] px-[18px] py-4">
        <h2 className="flex-1 text-[16px] font-semibold text-ink-100">Languages</h2>
        <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[18px] py-4">
        {!view ? (
          <p className="text-[13px] text-ink-300">Loading…</p>
        ) : blocked ? (
          <p className="text-[13px] leading-[1.5] text-ink-300">{blocked}</p>
        ) : (
          <>
            {available.length > 0 ? (
              <div className="flex flex-col gap-2">
                <span className="text-[12px] font-semibold text-ink-300">Pick languages</span>
                {offered.some((one) => chosen.includes(one.code)) && versionNumber !== undefined && previous ? (
                  <p className="text-[12px] leading-[1.5] text-ink-200">
                    v{previous.number} was made in {offered.map((one) => one.name).join(", ")}. They are chosen below
                    for this version too, at this version's prices.
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Languages to translate into">
                  {available.map((language) => {
                    const on = chosen.includes(language.code);
                    return (
                      <button
                        key={language.code}
                        type="button"
                        className="chip h-auto min-h-[32px] gap-1.5 py-1.5"
                        data-selected={on}
                        aria-pressed={on}
                        title={language.name}
                        onClick={() => toggle(language.code)}
                      >
                        {on ? "✓ " : ""}
                        {language.nativeName}
                        {!language.wholeVideo && wholeOffered ? (
                          <span className="text-[10px] font-normal text-ink-400">· voice + subtitles</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : view.languages.length === 0 ? (
              <p className="text-[13px] text-ink-300">There is no language to translate this video into.</p>
            ) : null}

            {available.length > 0 && version && !wholeOffered ? (
              <p className="rounded-[10px] bg-bay-800 px-3 py-2.5 text-[12px] leading-[1.5] text-ink-200">
                This version was made before its picture could be translated, so its languages are voice and subtitles.
                A new version can be translated whole.
              </p>
            ) : null}

            {available.length > 0 && wholeOffered ? (
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-2 text-[12px] font-semibold text-ink-300">How much to translate</legend>
                {(
                  [
                    ["voice", "Voice and subtitles", "The picture stays in English."],
                    [
                      "video",
                      "The whole video",
                      version
                        ? "Voice, subtitles and every word on screen."
                        : "Voice, subtitles, title and end cards, captions, and callouts.",
                    ],
                  ] as const
                ).map(([value, title, detail]) => {
                  const price = cardPrice(value);
                  const on = depth === value;
                  return (
                    <label
                      key={value}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-[10px] p-3 ${
                        on ? "border-2 border-ember-400 bg-ember-400/10" : "border border-[var(--edge-strong)]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="depth"
                        className="mt-[3px]"
                        checked={on}
                        onChange={() => setDepth(value)}
                      />
                      <span className="flex flex-1 flex-col gap-0.5">
                        <span className="text-[14px] font-medium text-ink-100">{title}</span>
                        <span className="text-[12px] leading-[1.45] text-ink-300">{detail}</span>
                      </span>
                      {price !== null ? (
                        <span className="tnum text-[13px] text-ink-100">{priceLabel(price, free)}</span>
                      ) : null}
                    </label>
                  );
                })}
                {note ? (
                  <p className="rounded-[10px] bg-bay-800 px-3 py-2.5 text-[12px] leading-[1.5] text-ink-200">{note}</p>
                ) : null}
                <p className="text-[12px] text-ink-400">Price per language.</p>
              </fieldset>
            ) : null}

            {available.length > 0 ? <KeepTerms projectId={projectId} /> : null}

            {available.length > 0 ? (
              <label className="flex items-center gap-2 text-[13px] text-ink-200">
                <input type="checkbox" checked={review} onChange={(event) => setReview(event.target.checked)} />
                Let me check each script before it is voiced
              </label>
            ) : null}
          </>
        )}

        {view && view.languages.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {view.languages.map((language) => (
              <li key={language.code}>
                <StatusCard
                  language={language}
                  version={version}
                  free={free}
                  working={working}
                  onCheckScript={() => onCheckScript(language.code)}
                  onVoice={() => void act(() => api.voiceLanguage(projectId, language.code, version))}
                  onUpdate={() => void act(() => api.updateLanguage(projectId, language.code, { review }, version))}
                  onRetry={() => void act(() => api.makeLanguages(projectId, retryBody(language, review, version)))}
                  onRaise={() => void act(() => api.updateLanguage(projectId, language.code, raiseBody(), version))}
                  onDiscard={async () => {
                    try {
                      await api.discardLanguage(projectId, language.code, version);
                      billingChanged();
                    } finally {
                      onChanged();
                    }
                  }}
                />
              </li>
            ))}
          </ul>
        ) : null}

        {version && view?.languages.some((one) => downloadLinks(one).length > 0) ? (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              className="btn btn-secondary btn-sm self-start"
              disabled={zipping}
              onClick={() => void downloadAll()}
            >
              {zipping ? "Preparing…" : "Download all · .zip"}
            </button>
            {zipError ? <span className="text-[11px] text-signal-bad">{zipError}</span> : null}
          </div>
        ) : null}

        {refusal ? (
          <ErrorNote>
            {refusal.message}
            {refusal.credit ? (
              <>
                {" "}
                <Link to="/settings#billing" className="underline">
                  Add credit
                </Link>
              </>
            ) : null}
          </ErrorNote>
        ) : null}
      </div>

      {view && !blocked && available.length > 0 ? (
        <div className="flex flex-col gap-2.5 border-t border-[var(--edge)] bg-bay-850 px-[18px] py-3.5">
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="tnum text-ink-300">{breakdown}</span>
            {view.balanceUsd !== null && !free ? (
              <span className="tnum text-ink-300">Balance {formatUsd(view.balanceUsd)}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-primary min-h-[44px]"
            disabled={working || chosen.length === 0 || short}
            onClick={translate}
          >
            {working
              ? "Starting…"
              : chosen.length === 0
                ? "Pick a language"
                : offered.length > 0 &&
                    versionNumber !== undefined &&
                    sameLanguages(
                      chosen,
                      offered.map((one) => one.code),
                    )
                  ? offerLabel(
                      offered.map((one) => one.name),
                      versionNumber,
                      total,
                      free,
                    )
                  : `Translate ${chosen.length} ${chosen.length === 1 ? "language" : "languages"} · ${priceLabel(total, free)}`}
          </button>
          {short ? (
            <p className="text-[12px] text-ink-300">
              That is more than your balance.{" "}
              <Link to="/settings#billing" className="text-ember-600 underline">
                Add credit
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
};

/** One language's state and what can be done about it. */
const StatusCard: React.FC<{
  language: LanguageView;
  version?: string;
  /** A free-forever team: prices read "Free" and nothing is held. */
  free: boolean;
  working: boolean;
  onCheckScript: () => void;
  onVoice: () => void;
  onUpdate: () => void;
  onRetry: () => void;
  onRaise: () => void;
  onDiscard: () => Promise<void>;
}> = ({ language, version, free, working, onCheckScript, onVoice, onUpdate, onRetry, onRaise, onDiscard }) => {
  const name = (
    <span className="flex-1 text-[14px] font-semibold text-ink-100">
      {language.nativeName}
      {language.nativeName !== language.name ? (
        <span className="ml-1.5 text-[12px] font-normal text-ink-400">{language.name}</span>
      ) : null}
    </span>
  );
  const card = "flex flex-col gap-2.5 rounded-[12px] border border-[var(--edge)] p-3";

  if (isInProgress(language.status)) {
    const text =
      language.status === "translating"
        ? "Translating…"
        : language.status === "voicing"
          ? "Voicing…"
          : "Voicing your changes…";
    return (
      <div className={card}>
        <div className="flex items-center gap-2">
          {name}
          <span className="text-[12px] text-ink-300">{text}</span>
        </div>
        {language.status !== "revoicing" && !free ? (
          <p className="text-[12px] text-ink-300">Holding {formatUsd(language.priceUsd)}</p>
        ) : null}
        <ProgressLine
          id={language.jobId ?? `language-${language.code}`}
          label={languageStepLabel(language)}
          progress={language.progress}
          eta={language.eta}
        />
      </div>
    );
  }

  if (language.status === "failed") {
    return (
      <div className={card}>
        <div className="flex items-center gap-2">
          {name}
          <span className="text-[12px] font-semibold text-signal-bad">Failed</span>
        </div>
        <p className="text-[12px] leading-[1.5] text-ink-300">{language.error}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={working || !language.canRemake}
            onClick={onRetry}
          >
            Try again · {priceLabel(language.priceUsd, free)}
          </button>
          {language.canDiscard ? <DiscardButton language={language} onDiscard={onDiscard} /> : null}
        </div>
      </div>
    );
  }

  if (language.outOfDate) {
    return (
      <div className={card}>
        <div className="flex items-center gap-2">
          {name}
          <span className="text-[12px] font-semibold text-signal-warn">Out of date: the English changed</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={working || !language.canRemake}
            onClick={onUpdate}
          >
            Update · {priceLabel(language.priceUsd, free)}
          </button>
          {language.canDiscard ? <DiscardButton language={language} onDiscard={onDiscard} /> : null}
        </div>
      </div>
    );
  }

  if (language.status === "review") {
    return (
      <div className="flex flex-col gap-2.5 rounded-[12px] border-2 border-ember-400 bg-ember-400/10 p-3">
        <div className="flex items-center gap-2">
          {name}
          <span className="text-[12px] font-semibold text-ember-600">Check the script</span>
        </div>
        <p className="text-[12px] leading-[1.5] text-ink-300">
          {language.runsLong > 0 ? `${lineWord(language.runsLong)}. ` : ""}
          {version ? "Read it, edit it, then approve." : "Read it in the editor, edit it there, then approve."}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCheckScript}>
            Check the script
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={working} onClick={onVoice}>
            Approve and voice
          </button>
          {language.canDiscard ? <DiscardButton language={language} onDiscard={onDiscard} /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={card}>
      <div className="flex items-center gap-2">
        {name}
        <span className="text-[12px] font-semibold text-signal-ok">Ready</span>
      </div>
      <p className="text-[12px] leading-[1.5] text-ink-300">
        {language.depth === "video" ? "The whole video" : "Voice and subtitles"}
        {language.runsLong > 0 ? ` · ${lineWord(language.runsLong)}` : ""}
      </p>
      {version && downloadLinks(language).length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
          {downloadLinks(language).map((link) => (
            <a key={link.kind} className="text-ember-600 hover:underline" href={link.href} download>
              {link.label}
            </a>
          ))}
        </div>
      ) : null}
      {language.depth === "voice" && language.wholeVideo ? (
        <div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={working || !language.canRaise}
            title={!working && !language.canRaise ? "Not enough credit to translate the whole video." : undefined}
            onClick={onRaise}
          >
            Translate the whole video · {priceLabel(language.videoPriceUsd, free)}
          </button>
        </div>
      ) : null}
    </div>
  );
};
